import { Storage } from "@lib/storage";
import { PersistedEngineState } from "./types";

const ENGINE_STATE_VERSION = "v1";
const PENDING_BLOB_VERSION = "v1";

/**
 * Handles persistence of AudioEngine state to IndexedDB.
 * All keys are prefixed with songId for easy retrieval and cleanup.
 */
export class EngineStorage {
  private storage: Storage;
  private songId: string;

  constructor(songId: string) {
    this.songId = songId;
    this.storage = new Storage();
  }

  /**
   * Get persisted state for this song, or null if not found
   */
  async getState(): Promise<PersistedEngineState | null> {
    const key = this.getKey();
    return await this.storage.getItem<PersistedEngineState>(key);
  }

  /**
   * Persist state for this song
   */
  async setState(state: PersistedEngineState): Promise<void> {
    const key = this.getKey();
    await this.storage.setItem(key, state);
  }

  /**
   * Remove persisted state for this song
   */
  async removeState(): Promise<void> {
    const key = this.getKey();
    await this.storage.removeItem(key);
  }

  // ============================================================================
  // Pending Blob Storage
  // ============================================================================

  /**
   * Store a pending blob for a region
   */
  async setPendingBlob(regionId: string, blob: Blob): Promise<void> {
    const key = this.getPendingBlobKey(regionId);
    await this.storage.setItem(key, blob);
  }

  /**
   * Get a pending blob for a region, or null if not found
   */
  async getPendingBlob(regionId: string): Promise<Blob | null> {
    const key = this.getPendingBlobKey(regionId);
    return await this.storage.getItem<Blob>(key);
  }

  /**
   * Remove a pending blob for a region
   */
  async removePendingBlob(regionId: string): Promise<void> {
    const key = this.getPendingBlobKey(regionId);
    await this.storage.removeItem(key);
  }

  /**
   * Remove multiple pending blobs for regions
   */
  async removePendingBlobs(regionIds: string[]): Promise<void> {
    await Promise.all(
      regionIds.map(regionId => this.removePendingBlob(regionId))
    );
  }

  // Key format: <songId>/engine-state-v1
  private getKey(): string {
    return `${this.songId}/engine-state-${ENGINE_STATE_VERSION}`;
  }

  // Key format: <songId>/pending-blob-v1-<regionId>
  private getPendingBlobKey(regionId: string): string {
    return `${this.songId}/pending-blob-${PENDING_BLOB_VERSION}-${regionId}`;
  }
}
